import { ArticleTechnicalInfoPropertyDef } from '@article/constants/article-technical-info-property';
import { ArticleTechnicalInfoManager } from '@article/manager/article-technical-info.manager';
import { ArticleVO } from '@article/model/vo/article.vo';
import { LocalizedPropertyValueVO, PropertyInLocalesVO } from '@project/components/article/model/vo/property.vo';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArrayUtils } from '@shared/util/array-utils';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class LocalizedPropertyService {
  constructor(
    private localeResourceManager: LocaleResourceManager,
    private articleTechnicalInfoManager: ArticleTechnicalInfoManager,
  ) {}

  /** @returns the first localized property value matching the given propertyName */
  public getPropertyValue(
    localizedProperties: PropertyInLocalesVO[],
    propertyName: string,
    locale: string | undefined = undefined,
  ): LocalizedPropertyValueVO | undefined {
    const propertyValue = this.getFirstValueByName(
      localizedProperties,
      propertyName,
      locale,
    );
    if (!propertyValue) {
      return undefined;
    }
    return propertyValue;
  }

  /** @returns the localized string value of the property matching the given propertyName */
  public getStringValue(localizedProperties: PropertyInLocalesVO[], propertyName: string, locale: string | undefined = undefined): string {
    return this.getPropertyValue(localizedProperties, propertyName, locale)?.stringValue ?? '';
  }

  /** @returns the english string value of the property matching the given propertyName */
  public getEnglishStringValue(localizedProperties: PropertyInLocalesVO[], propertyName: string): string {
    return this.getPropertyValue(localizedProperties, propertyName, 'en_US')?.stringValue ?? '';
  }


  /** @returns the localized float value of the property matching the given propertyName */
  public floatValue(
    localizedProperties: PropertyInLocalesVO[],
    propertyName: string,
  ): number {
    const propertyValue = this.getFirstValueByName(
      localizedProperties,
      propertyName,
    );
    if (!propertyValue) {
      return NaN;
    }
    return propertyValue.floatValue;
  }

  /** @returns the first LocalizedPropertyValueVO by name for the current locale chain */
  public getFirstValueByName(
    localizedProperties: PropertyInLocalesVO[],
    propertyName: string,
    locale: string | undefined = undefined,
  ): LocalizedPropertyValueVO | undefined {
    const propertyInLocales = this.getPropertyInLocalesByName(localizedProperties, propertyName);
    if(!propertyInLocales) return undefined;
    return this.getFirstValueMatchingLocaleChain(propertyInLocales, locale);
  }

  /** @returns the first LocalizedPropertyValueVO with the given locale. If locale is undefined, returns the first LocalizedPropertyValueVO */
  public getFirstValueWithLocale(localizedProperty: PropertyInLocalesVO, locale: string | undefined): LocalizedPropertyValueVO | undefined {
    if (!localizedProperty || ArrayUtils.isEmpty(localizedProperty.values)) {
      return undefined;
    }
    if (!locale) {
      return localizedProperty.values[0];
    }
    const unlocalizedValues: LocalizedPropertyValueVO[] = []; // property values where locale == null
    for (const localizedValue of localizedProperty.values) {
      if (localizedValue.locale === locale) {
        return localizedValue;
      }
      if (!localizedValue.locale) {
        unlocalizedValues.push(localizedValue);
      }
    }
    // return value without locale if localized value not found
    return unlocalizedValues.length > 0 ? unlocalizedValues[0] : undefined;
  }

  /** @returns the first LocalizedPropertyValueVO matching the current locale chain  */
  public getFirstValueMatchingLocaleChain(localizedProperty: PropertyInLocalesVO, locale: string | undefined = undefined): LocalizedPropertyValueVO | undefined{
    if (!localizedProperty || ArrayUtils.isEmpty(localizedProperty.values)) {
      return undefined;
    }
    const localeChain = locale ? [locale] : this.localeResourceManager.getLocaleChain();
    for (const localeInChain of localeChain) {
      const localizedPropertyValue = this.getFirstValueWithLocale(localizedProperty, localeInChain);
      if (localizedPropertyValue) {
        return localizedPropertyValue;
      }
    }
    return undefined;
  }

  /** @returns the PropertyInLocalesVO from localizedProperties matching the given name */
  public getPropertyInLocalesByName(localizedProperties: PropertyInLocalesVO[], propertyName: string): PropertyInLocalesVO | undefined {
    if (!localizedProperties || !propertyName) {
      return undefined;
    }
    for (const propertyInLocales of localizedProperties) {
      if (propertyInLocales && propertyInLocales.name === propertyName) {
        return propertyInLocales;
      }
    }
    return undefined;
  }

  public getColorNameFromArticleId(articleId: number | undefined): string {
    if(articleId === undefined) {
      return '';
    }
    const articleTechnicalInfoVOs = this.articleTechnicalInfoManager.getTechnicalInfo(articleId)?.articleTechnicalInfoVOs;
    return this.getStringValue(articleTechnicalInfoVOs ?? [], ArticleTechnicalInfoPropertyDef.COLOR);
  }

  public getEnglishColorNameFromArticleId(articleId: number | undefined): string {
    if (articleId === undefined) {
      return '';
    }
    const articleTechnicalInfoVOs = this.articleTechnicalInfoManager.getTechnicalInfo(articleId)?.articleTechnicalInfoVOs;
    return this.getEnglishStringValue(articleTechnicalInfoVOs ?? [], ArticleTechnicalInfoPropertyDef.COLOR);
  }

  public getColorNameFromArticle(article: ArticleVO | undefined): string {
    if(article === undefined) {
      return '';
    }
    return this.getStringValue(article?.preloadedTechnicalInfos ?? [], ArticleTechnicalInfoPropertyDef.COLOR);
  }

  public getEnglishColorNameFromArticle(article: ArticleVO | undefined): string {
    if(article === undefined) {
      return '';
    }
    return this.getEnglishStringValue(article?.preloadedTechnicalInfos ?? [], ArticleTechnicalInfoPropertyDef.COLOR);
  }
}
